import React, { type PropsWithChildren, useState, useCallback, useEffect, forwardRef } from 'react';
import { ExternalActivityListener } from '../external-activity-listener';
import type { MenuProps } from './Menu';
import Menu from './Menu';
import { RelativePortal } from '../relative-portal';
import type { RelativePortalProps } from '../relative-portal/RelativePortal';
import { cn } from '@/lib/utils';

export interface TriggerableMenuProps<V> extends MenuProps<V> {
  scroll?: number;
  innerClassName?: string;
  disabled?: boolean;
  usePortal?: boolean;
  /** Props passed to the portal component, only effective is usePortal is true */
  portalProps?: Partial<RelativePortalProps>;
  onToggle?: (open: boolean) => void;
  forceToggle?: boolean;
}

const TriggerableMenuComponent = <V,>(
  {
    className,
    children,
    style,
    innerClassName,
    onSelect,
    usePortal,
    portalProps,
    disabled,
    onToggle,
    forceToggle,
    ...menuProps
  }: PropsWithChildren<TriggerableMenuProps<V>>,
  ref: React.Ref<HTMLSpanElement>,
) => {
  const [open, setOpen] = useState(false);

  const onToggleOpen = useCallback(
    (newOpen: boolean) => {
      if (onToggle) {
        onToggle(newOpen);
      }
      setOpen(newOpen);
    },
    [onToggle],
  );

  useEffect(() => {
    if (forceToggle) {
      onToggleOpen(!open);
    }
  }, [forceToggle, open, onToggleOpen]);

  const handleToggle = useCallback(
    (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      event.stopPropagation();
      if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }
      if (!disabled) {
        onToggleOpen(!open);
      }
    },
    [disabled, open, onToggleOpen],
  );

  const handleKeyUp = useCallback((event: React.KeyboardEvent<HTMLSpanElement>) => {
    event.stopPropagation();
  }, []);

  const handleExternalActivity = useCallback(() => {
    onToggleOpen(false);
  }, [onToggleOpen]);

  const menuComponent = (
    <Menu
      shouldRender={open}
      className={cn(['absolute top-full left-0 bg-venn-white z-[1]', innerClassName])}
      onSelect={onSelect}
      style={style}
      {...menuProps}
    />
  );

  const renderedMenuComponent = usePortal
    ? open && <RelativePortal {...portalProps}>{menuComponent}</RelativePortal>
    : menuComponent;

  return (
    <span
      className={cn(['inline-flex relative items-center min-h-[36px]', className])}
      role="button"
      tabIndex={-1}
      onClick={handleToggle}
      onKeyUp={handleKeyUp}
      ref={ref}
    >
      <ExternalActivityListener listeningEnabled={open} onExternalActivity={handleExternalActivity}>
        {renderedMenuComponent}
        {children}
      </ExternalActivityListener>
    </span>
  );
};

export const TriggerableMenu = forwardRef(TriggerableMenuComponent) as <V>(
  props: React.PropsWithChildren<TriggerableMenuProps<V>> & { ref?: React.Ref<HTMLSpanElement> },
) => React.ReactElement;
export default TriggerableMenu;
